
import { Common } from '@icc/shared/helpers';
import {
    APP_CONFIG,
    AppConfigFactory,
    TranslateService,
    ColorsDefaultsService,
    core,
} from '@icc/shared';
import { Injectable, Inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { getDefaultShapeDimensions } from '@icc/shared/types';
import { ConfigurationType } from '@icc/shared/types';
import { SketchData } from '@icc/shared/types/complex/SketchData';
import { ConfiguratorsDataService } from './configurators-data.service';
import { ActiveConfiguration } from './configuration/active-configuration';
import { Configuration } from './configuration/configuration';
import { AbstractConfiguratorsRepositoryService as ConfiguratorsRepositoryService } from './configurators-repository.service';
import { ConfiguratorConfigurations } from './configuration/configurations';

export interface Configurations {
    [type: string]: any;
}

export type ConfigurationsKeys = Extract<keyof Configurations, string>;

export interface ConfiguratorEnvironment<T extends ConfigurationsKeys = ConfigurationsKeys> {
    editedPositionPrices?: {
        clientPrice: number;
        dealerPrice: number;
    }
    Configuration: Configurations[T]['active'];
    Current: Configurations[T]['active']['prototype'];
    Edit: Configurations[T]['active']['prototype'] & {
        _id: string
    } | boolean | undefined;
    Default: Configurations[T]['active']['prototype'];
    conf: T;
    copiedPosition: boolean;
    measurementsAttachments: any[];
    offerConfigurator: boolean;
    addPositionToGroup?: boolean;
    stepCode?: string;
    configuratorId?: number | undefined;
    usedStartState?: boolean;
}

export type ActiveConfigurationImpl = Configurations[ConfigurationsKeys]['active']['prototype'];
export type ConfigurationImpl = Configurations[ConfigurationsKeys]['simple']['prototype'];

@Injectable({
    providedIn: 'root',
})
export abstract class ConfigurationsService<T extends string = string> {
    abstract conf: ConfiguratorEnvironment<T>;
    conf$: BehaviorSubject<Partial<ConfiguratorEnvironment<T>>> = new BehaviorSubject<
        Partial<ConfiguratorEnvironment<T>>
    >({});
    configuration$ = this.conf$.pipe(
        filter(Common.isNotNullOrUndefined),
        map(conf => conf.Current),
        filter(Common.isNotNullOrUndefined)
    );
    price = { noPrice: null };
    version = 1;
    configurators: Partial<Record<ConfigurationsKeys, ConfiguratorEnvironment>> = {};
    systemsComparisonEdit = false;

    editedPosition: Partial<Record<ConfigurationsKeys, {
        _id: string,
        clientPrice?: number,
        dealerPrice?: number,
    } | null>> = {};
    copiedPosition = false;

    constructor(
        protected translateService: TranslateService,
        @Inject(APP_CONFIG) protected config: AppConfigFactory,
    ) {}

    getSimpleConfiguration<K extends string>(type: K, id = 0): ConfiguratorConfigurations['simple'] | null {
        const configurations = ConfiguratorsRepositoryService.getConfigurations(type, id);
        if (configurations) {
            return configurations.simple;
        }
        return null;
    }

    getActiveConfiguration<K extends string>(type: K, id = 0): ConfiguratorConfigurations['active'] | null {
        const configurations = ConfiguratorsRepositoryService.getConfigurations(type, id);
        if (configurations) {
            return configurations.active;
        }
        return null;
    }

    createSimpleConfiguration(
        configuration: ActiveConfiguration | Configuration,
        dataRequiredForUpdate: any = {},
        configuratorsDataService?: ConfiguratorsDataService,
        langCode: string = this.config().CurLang,
        config = this.config()
    ) {
        const simpleConfiguration = this.getSimpleConfiguration(configuration.type, configuration.configuratorId ?? 0);
        if (simpleConfiguration) {
            return new simpleConfiguration(
                configuration,
                dataRequiredForUpdate,
                true,
                configuratorsDataService,
                langCode,
                config
            );
        }
        return null;
    }

    createActiveConfiguration(
        configuration?: ActiveConfiguration | Configuration | null,
        configuratorsDataService?: ConfiguratorsDataService,
        langCode?: string,
    ) {
        if (configuration) {
            const activeConfiguration = this.getActiveConfiguration(configuration.type, configuration.configuratorId ?? 0);
            if (activeConfiguration) {
                return new activeConfiguration(configuration, configuratorsDataService, langCode);
            }
        } else {
            const activeConfiguration = this.getActiveConfiguration(this.conf.conf, this.conf.configuratorId);
            if (activeConfiguration) {
                return this.setDefaults(new activeConfiguration());
            }
        }
        return null;
    }

    setDefaults(
        configuration: Configurations[ConfigurationType]['active']['prototype'],
        newConfiguration = true,
        configuratorId: number | null = 0,
    ) {

        switch (configuration.type) {
            case 'window':
                if (newConfiguration) {
                    configuration.Width = this.config().IccConfig.Configurators.window.dimensions.width || 1500;
                    configuration.Height = this.config().IccConfig.Configurators.window.dimensions.height || 1500;
                    configuration.Shape = getDefaultShapeDimensions('rect', configuration.Width, configuration.Height);
                }
                configuration.Steel = this.config().IccConfig.Configurators.steelSelect
                    ? 'Opened'
                    : null;
                configuration.DrawId = 'draw_' + core.generateUUID();
                configuration.RollerShutter.hasAssembly = this.config().IccConfig.Configurators.roller_shutter.defaultAssemblyToWindow;
                break;
            case 'hs':
                if (newConfiguration) {
                    configuration.Width = this.config().IccConfig.Configurators.hs.dimensions.width || 2000;
                    configuration.Height = this.config().IccConfig.Configurators.hs.dimensions.height || 2000;
                    configuration.Shape = getDefaultShapeDimensions('rect', configuration.Width, configuration.Height);
                }
                configuration.Steel = this.config().IccConfig.Configurators.steelSelect
                    ? 'Opened'
                    : null;
                configuration.DrawId = 'draw_' + core.generateUUID();
                configuration.RollerShutter.hasAssembly = this.config().IccConfig.Configurators.roller_shutter.defaultAssemblyToWindow;
                break;
            case 'sliding_door':
                if (newConfiguration) {
                    configuration.Width = this.config().IccConfig.Configurators.sliding_door.dimensions.width || 2000;
                    configuration.Height = this.config().IccConfig.Configurators.sliding_door.dimensions.height || 2000;
                    configuration.Shape = getDefaultShapeDimensions('rect', configuration.Width, configuration.Height);
                }
                configuration.Steel = this.config().IccConfig.Configurators.steelSelect
                    ? 'Opened'
                    : null;
                configuration.DrawId = 'draw_' + core.generateUUID();
                configuration.RollerShutter.hasAssembly = this.config().IccConfig.Configurators.roller_shutter.defaultAssemblyToWindow;
                break;
            case 'door':
                if (newConfiguration) {
                    configuration.Width = this.config().IccConfig.Configurators.door.dimensions.width || 1000;
                    configuration.Height = this.config().IccConfig.Configurators.door.dimensions.height || 2000;
                    configuration.Shape = getDefaultShapeDimensions('rect', configuration.Width, configuration.Height);
                    configuration.Quantity = this.config().quantity || 1;
                    configuration.Title = this.config().userDescription || '';
                }
                configuration.RollerShutter.hasAssembly = this.config().IccConfig.Configurators.roller_shutter.defaultAssemblyToWindow;
                break;
            case 'accessory':
                configuration.Name = this.translateService.instant('ACCESSORY|Akcesoria');
                break;
            case 'complementary_goods':
                configuration.Name = this.config().IccConfig.Configurators.complementary_goods
                    .namedAccessory
                    ? this.translateService.instant('ACCESSORY|Akcesoria')
                    : this.translateService.instant('ACCESSORY|Dobra komplementarne');
                break;
            case 'coupled_window':
                configuration.Name = this.translateService.instant('COUPLED_WINDOW|Konstrukcje złożone');
                break;
            case 'roller_shutter':
                if (newConfiguration) {
                    configuration.Width = this.config().IccConfig.Configurators.roller_shutter?.dimensions?.width || 1500;
                    configuration.Height = this.config().IccConfig.Configurators.roller_shutter?.dimensions?.height || 1500;
                }

                if (configuration?.RollerShutter?.Accessories) {
                    configuration.Accessories = [...configuration?.RollerShutter?.Accessories];
                }
                break;
            case 'complex':
                if (newConfiguration) {
                    configuration.configuratorId = configuratorId;
                }

                break;
        }
        return configuration;
    }

    updatedConfiguration() {
        this.conf$.next(this.conf);
    }

    setConstructionName(name: string){
        this.conf.Current.Title=name;
        this.updatedConfiguration();
    }

    getConstructionName() {
        return this.conf.Current.Title || '';
    }

    setQuantity(quantity = 1) {
        if (!this.conf?.Current) {
            return;
        }

        if (quantity > 0) {
            this.conf.Current.Quantity = quantity;
            this.updatedConfiguration();
        } else {
            this.conf.Current.Quantity = 1;
        }
    }

    setProductId(productId:number){
        if(
            this.conf.Current.type === 'complex'
        ){
            this.conf.Current.productId = productId;
        }

    }

    setProductExtId(productExtId:string){
        if(
            this.conf.Current.type === 'complex'
        ){
            this.conf.Current.productExtId = productExtId;
        }

    }

    setProductName(productName:string){
        if(
            this.conf.Current.type === 'complex'
        ){
            this.conf.Current.productName = productName;
        }

    }

    setConfiguratorName(configuratorName:string){
        if(
            this.conf.Current.type === 'complex'
        ){
            this.conf.Current.configuratorName = configuratorName;
        }
    }

    clearSketchMainData(){
        if(
            this.conf.Current.type === 'complex'
        ){
            this.conf.Current.sketchMainData = null;
        }

    }

    setSketchMainData(sketchData: SketchData) {
        if (!this.conf?.Current || !sketchData) {
            return;
        }

        if(
            this.conf.Current.type === 'complex'
        ){
            this.conf.Current.sketchMainData = sketchData;
            this.updatedConfiguration();
        }
    }

    clearSketchAdditionalData(){
        if(
            this.conf.Current.type === 'complex'
        ){
            this.conf.Current.sketchAdditionalData = [];
        }
    }

    setSketchAdditionalData(sketchData:string) {
        if (!this.conf?.Current || !sketchData) {
            return;
        }

        if(
            this.conf.Current.type === 'complex'
        ){
            this.conf.Current.sketchAdditionalData.push(sketchData);
            this.updatedConfiguration();
        }
    }

    getQuantity() {
        return this.conf.Current.Quantity;
    }

    abstract getOrInitConfiguratorConfigurations<U extends ConfigurationsKeys>(
        type: U,
        startState?:
            | InstanceType<Configurations[U]['active']>
            | InstanceType<Configurations[U]['simple']>
    ): ConfiguratorEnvironment<U>;
}
